import React, { useState, useEffect } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { unitsAPI } from '../../lib/api';
import { Buildings, MapPin, Users, ArrowRight } from '@phosphor-icons/react';

const StructurePage = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await unitsAPI.getAll();
        setUnits(response.data);
      } catch (error) {
        console.log('Error fetching units');
      } finally {
        setLoading(false);
      }
    };
    fetchUnits();
  }, []);

  const commanderies = units.filter(u => u.type === 'commandery');
  const prefectories = units.filter(u => u.type === 'prefectory');
  const committees = units.filter(u => u.type === 'committee');

  return (
    <PublicLayout>
      {/* Header */}
      <section className="py-20 bg-[#1A1614] text-center" data-testid="structure-header">
        <p className="text-overline mb-4">Organizational Framework</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
          Structure & Offices
        </h1>
        <p className="text-[#8A847A] max-w-2xl mx-auto px-4">
          The hierarchical organization of the Grand Priory of the Caribbean
        </p>
      </section>

      {/* Hierarchy Overview */}
      <section className="py-16 bg-white" data-testid="hierarchy-overview">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl text-[#1A1614] mb-4">Organizational Hierarchy</h2>
            <p className="text-[#4A453E]">
              The Grand Priory operates under a structured hierarchy that ensures 
              effective governance and administration throughout the Caribbean.
            </p>
          </div>

          {/* Hierarchy Diagram */}
          <div className="space-y-8">
            {/* Grand Priory */}
            <div className="bg-[#7B1818] text-white p-6 text-center">
              <p className="text-overline text-[#C5A059] mb-2">Supreme Authority</p>
              <h3 className="font-heading text-2xl">Grand Priory of the Caribbean</h3>
              <p className="text-sm opacity-80 mt-2">Led by the Grand Prior under the Grand Magisterium</p>
            </div>

            <div className="flex justify-center">
              <ArrowRight size={32} className="text-[#C5A059] rotate-90" />
            </div>

            {/* Grand Chapter */}
            <div className="bg-[#1A1614] text-white p-6 text-center">
              <p className="text-overline text-[#C5A059] mb-2">Governing Body</p>
              <h3 className="font-heading text-xl">The Grand Chapter</h3>
              <p className="text-sm opacity-80 mt-2">Composed of Grand Officers who ensure proper administration, governance, and spiritual direction</p>
            </div>

            <div className="flex justify-center">
              <ArrowRight size={32} className="text-[#C5A059] rotate-90" />
            </div>

            {/* Commanderies */}
            <div className="bg-[#F5F2EB] border border-[#D8D2C4] p-6">
              <div className="flex items-center gap-4 mb-4">
                <Buildings size={32} className="text-[#7B1818]" />
                <div>
                  <h3 className="font-heading text-xl text-[#1A1614]">Commanderies</h3>
                  <p className="text-sm text-[#4A453E]">Regional administrative units led by Commanders</p>
                </div>
              </div>
              {commanderies.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {commanderies.map(cmd => (
                    <div key={cmd.id} className="bg-white p-4 border border-[#D8D2C4]">
                      <p className="font-medium text-[#1A1614]">{cmd.name}</p>
                      {cmd.region && (
                        <p className="text-sm text-[#8A847A] flex items-center gap-1 mt-1">
                          <MapPin size={14} /> {cmd.region}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#8A847A] mt-4">Commanderies are established as the Order grows in each region.</p>
              )}
            </div>

            <div className="flex justify-center">
              <ArrowRight size={32} className="text-[#C5A059] rotate-90" />
            </div>

            {/* Prefectories */}
            <div className="bg-[#F5F2EB] border border-[#D8D2C4] p-6">
              <div className="flex items-center gap-4 mb-4">
                <Users size={32} className="text-[#7B1818]" />
                <div>
                  <h3 className="font-heading text-xl text-[#1A1614]">Prefectories</h3>
                  <p className="text-sm text-[#4A453E]">Local chapters within commanderies led by Prefects</p>
                </div>
              </div>
              {prefectories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  {prefectories.map(pref => (
                    <div key={pref.id} className="bg-white p-4 border border-[#D8D2C4]">
                      <p className="font-medium text-[#1A1614]">{pref.name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#8A847A] mt-4">Prefectories operate at the local level.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Grand Offices */}
      <section className="py-16 bg-[#F5F2EB]" data-testid="offices-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-8 text-center">Grand Offices</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Grand Prior', desc: 'Supreme head of the Grand Priory, representing the Order throughout the Caribbean region and serving as Grand Magisterial Legate to the Grand Magisterium.' },
              { title: 'Grand Chancellor', desc: 'Chief administrative officer overseeing official records, correspondence, and diplomatic affairs of the Grand Priory.' },
              { title: 'Grand Commander', desc: 'Leads the military and ceremonial aspects of the Order, ensuring proper protocol and discipline among the knights.' },
              { title: 'Grand Secretary General', desc: 'Maintains all official records, manages membership documentation, and oversees administrative operations.' },
              { title: 'Grand Treasurer', desc: 'Manages all financial matters of the Grand Priory, including dues, donations, and charitable disbursements.' },
              { title: 'Grand Magisterial Ambassador', desc: 'Represents the Grand Priory in diplomatic relations with the Grand Magisterium, other Orders, institutions, and international bodies.' },
              { title: 'Grand Herald', desc: 'Oversees all ceremonial protocols, processions, and the proper display of heraldic symbols and regalia of the Order.' },
              { title: 'Communications Officer', desc: 'Manages all internal and external communications, including media relations and public announcements.' },
            ].map(office => (
              <div key={office.title} className="bg-white border border-[#D8D2C4] p-6">
                <h3 className="font-heading text-lg text-[#1A1614] mb-2">{office.title}</h3>
                <p className="text-sm text-[#4A453E]">{office.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Council */}
      <section className="py-16 bg-[#1A1614]" data-testid="security-council-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-white mb-4 text-center">The Security Council</h2>
          <p className="text-[#8A847A] text-center mb-8 max-w-2xl mx-auto">
            The Security Council is responsible for the safety, integrity, and intelligence 
            operations of the Grand Priory. Members hold the rank of Sergeant and serve under 
            strict protocols.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Chief Intelligence Officer', desc: 'Leads the Security Council\'s intelligence operations, ensuring the safety and integrity of the Order.' },
              { title: 'Chief of Operations', desc: 'Oversees all operational activities of the Security Council, coordinating security measures and logistics.' },
              { title: 'Secretary', desc: 'Maintains records of all security proceedings and coordinates administrative functions of the Council.' },
              { title: 'Senior Councilor', desc: 'Serves as an advisory member of the Security Council, providing counsel on security and strategic matters.' },
            ].map(office => (
              <div key={office.title} className="bg-[#2A2420] border border-[#3A3430] p-6">
                <h3 className="font-heading text-lg text-white mb-2">{office.title}</h3>
                <p className="text-sm text-[#8A847A]">{office.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Committees */}
      <section className="py-16 bg-white" data-testid="committees-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-8 text-center">Standing Committees</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {committees.length > 0 ? (
              committees.map(comm => (
                <div key={comm.id} className="border-l-2 border-[#C5A059] pl-4">
                  <h3 className="font-heading text-lg text-[#1A1614]">{comm.name}</h3>
                  {comm.description && (
                    <p className="text-sm text-[#4A453E] mt-1">{comm.description}</p>
                  )}
                </div>
              ))
            ) : (
              <>
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <h3 className="font-heading text-lg text-[#1A1614]">Membership Committee</h3>
                  <p className="text-sm text-[#4A453E] mt-1">Reviews applications and advises on membership matters, ensuring candidates meet the standards of the Order.</p>
                </div>
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <h3 className="font-heading text-lg text-[#1A1614]">Charitable Works Committee</h3>
                  <p className="text-sm text-[#4A453E] mt-1">Coordinates humanitarian and charitable activities throughout the Caribbean region.</p>
                </div>
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <h3 className="font-heading text-lg text-[#1A1614]">Protocol Committee</h3>
                  <p className="text-sm text-[#4A453E] mt-1">Ensures proper ceremonial and diplomatic protocols are maintained at all Order functions.</p>
                </div>
                <div className="border-l-2 border-[#C5A059] pl-4">
                  <h3 className="font-heading text-lg text-[#1A1614]">Communications Committee</h3>
                  <p className="text-sm text-[#4A453E] mt-1">Manages official communications, media relations, and public announcements of the Grand Priory.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default StructurePage;
